import { ref, watch, onUnmounted } from 'vue';
import useIsMobile from './useIsMobile';

export interface KeyboardAvoidanceOptions {
  /** Minimum visible modal height ratio before falling back to scroll (0–1). Default: 0.4 */
  minVisibleRatio?: number;
}

// Module-level: only one modal can be active at a time (HarpiaWMS design constraint)
const activeModalEl = ref<HTMLElement | null>(null);

export function useKeyboardAvoidance(options: KeyboardAvoidanceOptions = {}) {
  const minVisibleRatio = options.minVisibleRatio ?? 0.4;

  const { isKeyboardOpen, keyboardHeight, isMobileDevice } = useIsMobile();

  const isActive = ref(false);

  // Active input element for scroll-into-view fallback
  let activeInput: HTMLElement | null = null;

  /**
   * Removes the keyboard avoidance class and resets the CSS variable.
   * Called both on keyboard close and on detach.
   */
  function removeKeyboardAvoidance() {
    if (!activeModalEl.value) return;
    activeModalEl.value.classList.remove('r3-keyboard-avoiding');
    activeModalEl.value.style.setProperty('--keyboard-offset', '');
    isActive.value = false;
  }

  /**
   * Applies keyboard avoidance by adding the CSS class and setting the offset.
   */
  function applyKeyboardAvoidance() {
    if (!activeModalEl.value || !isMobileDevice.value) return;

    activeModalEl.value.classList.add('r3-keyboard-avoiding');
    activeModalEl.value.style.setProperty('--keyboard-offset', `-${keyboardHeight.value}px`);
    isActive.value = true;
  }

  /**
   * Registers a modal element for keyboard avoidance.
   * Safe to call multiple times — previous modal is automatically replaced.
   */
  function attach(modalRootEl: HTMLElement) {
    if (!import.meta.client) return;

    // Clean up any previous modal before registering the new one
    if (activeModalEl.value && activeModalEl.value !== modalRootEl) {
      removeKeyboardAvoidance();
    }

    activeModalEl.value = modalRootEl;
    activeInput = modalRootEl.querySelector<HTMLElement>(':focus') ?? null;
  }

  /**
   * Removes all keyboard avoidance state and cleans up.
   */
  function detach() {
    removeKeyboardAvoidance();
    activeModalEl.value = null;
    activeInput = null;
  }

  // Watch keyboard open/close state
  watch(isKeyboardOpen, (open) => {
    if (!activeModalEl.value || !isMobileDevice.value) return;

    if (open) {
      // Find the currently focused input inside the modal for scroll fallback
      activeInput = activeModalEl.value.querySelector<HTMLElement>(':focus') ?? null;

      const modalHeight = activeModalEl.value.getBoundingClientRect().height;
      const viewportHeight = window.innerHeight;
      const keyboardH = keyboardHeight.value;

      // Compute remaining visible height after applying push transform
      const remainingHeight = viewportHeight - keyboardH;

      const visibleRatio = modalHeight > 0 ? remainingHeight / modalHeight : 1;

      if (visibleRatio < minVisibleRatio) {
        // Fallback: don't push, scroll the active input into view instead
        if (activeInput) {
          activeInput.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      } else {
        // Push: apply transform via CSS class + variable
        applyKeyboardAvoidance();
      }
    } else {
      // Keyboard closed: remove avoidance
      removeKeyboardAvoidance();
    }
  });

  // Cleanup on component unmount
  onUnmounted(() => {
    detach();
  });

  return {
    attach,
    detach,
    isActive,
  };
}
